import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut,
  Sparkles,
  Send,
  Loader2,
  CheckCircle2,
  RefreshCcw,
  Mic,
  Bug,
  Lightbulb,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import VoiceRecorder from "@/components/VoiceRecorder";
import AudioUpload from "@/components/AudioUpload";
import AttachmentUpload from "@/components/AttachmentUpload";
import BugPreviewForm from "@/components/BugPreviewForm";
import {
  clearToken,
  fetchConfig,
  generateBugReport,
  submitBug,
  transcribeAudio,
  uploadAttachment,
} from "@/lib/api";

const EMPTY_BUG = {
  title: "",
  restaurant_id: "Not provided",
  restaurant_name: "Not provided",
  what_trying_to_do: "Not provided",
  what_happened: "",
  what_should_have_happened: "Not provided",
  when_did_this_happen: "Not provided",
  attachment: "Not provided",
};

export default function BugIntakePage() {
  const nav = useNavigate();

  const [recordedAudio, setRecordedAudio] = useState(null);
  const [uploadedAudio, setUploadedAudio] = useState(null);
  const [attachment, setAttachment] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [bug, setBug] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [defaults, setDefaults] = useState({
    default_restaurant_id: "",
    default_restaurant_name: "",
  });

  useEffect(() => {
    fetchConfig()
      .then((d) => setDefaults(d || {}))
      .catch(() => {});
  }, []);

  const audioFile = recordedAudio || uploadedAudio;
  const attachmentKind = useMemo(() => {
    if (!attachment) return null;
    return attachment.type?.startsWith("video/") ? "video" : "screenshot";
  }, [attachment]);

  const onLogout = () => {
    clearToken();
    nav("/login", { replace: true });
  };

  const onRecordedChange = (f) => {
    setRecordedAudio(f);
    if (f) setUploadedAudio(null);
  };
  const onUploadedChange = (f) => {
    setUploadedAudio(f);
    if (f) setRecordedAudio(null);
  };

  const onGenerate = async () => {
    if (!audioFile) {
      toast.error("Please record or upload a voice message.");
      return;
    }
    setGenerating(true);
    setBug(null);
    try {
      toast.message("Transcribing your voice…");
      const { transcription } = await transcribeAudio(audioFile);
      if (!transcription) {
        toast.error("We couldn't hear anything clearly. Please try again.");
        setGenerating(false);
        return;
      }
      toast.message("Generating bug report…");
      const generated = await generateBugReport(transcription, attachmentKind);

      // Apply env defaults client-side as a safety net
      if (
        defaults.default_restaurant_id &&
        (!generated.restaurant_id ||
          generated.restaurant_id.trim().toLowerCase() === "not provided")
      ) {
        generated.restaurant_id = defaults.default_restaurant_id;
      }
      if (
        defaults.default_restaurant_name &&
        (!generated.restaurant_name ||
          generated.restaurant_name.trim().toLowerCase() === "not provided")
      ) {
        generated.restaurant_name = defaults.default_restaurant_name;
      }

      // Override attachment field with precise value
      if (attachmentKind === "screenshot") {
        generated.attachment = "Screenshot attached";
      } else if (attachmentKind === "video") {
        generated.attachment = "Video attached";
      } else if (
        !generated.attachment ||
        generated.attachment.trim().toLowerCase() === "not provided"
      ) {
        generated.attachment = "Not provided";
      }

      setBug({ ...EMPTY_BUG, ...generated });
      toast.success("Bug report ready. Please review before submitting.");
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail || "Failed to generate bug report. Please try again.";
      toast.error(detail);
    } finally {
      setGenerating(false);
    }
  };

  const onSubmit = async () => {
    if (!bug) return;
    if (!bug.title?.trim() || bug.title.trim().toLowerCase() === "not provided") {
      toast.error("Title is required.");
      return;
    }
    if (
      !bug.what_happened?.trim() ||
      bug.what_happened.trim().toLowerCase() === "not provided"
    ) {
      toast.error("'What happened' is required.");
      return;
    }

    setSubmitting(true);
    try {
      let attachmentFilename = null;
      if (attachment) {
        toast.message("Uploading attachment…");
        const up = await uploadAttachment(attachment);
        attachmentFilename = up.filename;
      }
      await submitBug(bug, attachmentFilename);
      toast.success("Bug submitted successfully.");
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      const detail =
        err?.response?.data?.detail || "Submission failed. Please try again.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const startFresh = () => {
    setRecordedAudio(null);
    setUploadedAudio(null);
    setAttachment(null);
    setBug(null);
    setSubmitted(false);
  };

  // ---------- Success screen ----------
  if (submitted) {
    return (
      <div className="min-h-screen bg-bone" data-testid="bug-success-page">
        <Header onLogout={onLogout} />
        <main className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
          <div className="bg-white border border-sage rounded-2xl shadow-ambient p-10 text-center animate-fade-up">
            <span className="h-16 w-16 mx-auto grid place-items-center rounded-full bg-[#7B9672]/15 text-[#7B9672]">
              <CheckCircle2 className="w-8 h-8" strokeWidth={1.6} />
            </span>
            <h2 className="font-display text-3xl text-ink mt-5">
              Bug submitted successfully.
            </h2>
            <p className="text-ink-muted mt-2 max-w-md mx-auto">
              Thanks for the report! The team has received the details and will take a
              look.
            </p>
            <Button
              onClick={startFresh}
              data-testid="report-another-button"
              className="mt-8 h-12 px-6 rounded-xl bg-terracotta hover:bg-[#C06555] active:bg-[#A85346] text-white"
            >
              <RefreshCcw className="w-4 h-4 mr-2" strokeWidth={1.8} />
              Report another bug
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone" data-testid="bug-intake-page">
      <Header onLogout={onLogout} />

      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        {/* Intake type selector (Phase 2: Change Request) */}
        <section
          className="mb-8 grid grid-cols-1 sm:grid-cols-2 gap-3 animate-fade-up"
          data-testid="intake-type-selector"
        >
          <button
            type="button"
            data-testid="intake-type-bug"
            aria-pressed="true"
            className="group relative flex items-center gap-3 px-5 py-4 rounded-2xl bg-white border-2 border-[#D47A6A] shadow-ambient text-left transition-transform hover:-translate-y-0.5"
          >
            <span className="h-10 w-10 grid place-items-center rounded-xl bg-terracotta text-white shrink-0">
              <Bug className="w-5 h-5" strokeWidth={1.7} />
            </span>
            <div className="min-w-0">
              <p className="font-display text-base text-ink leading-tight">
                Report a Bug
              </p>
              <p className="text-xs text-ink-muted truncate">
                Voice → structured report
              </p>
            </div>
            <span className="ml-auto text-[10px] tracking-[0.18em] uppercase font-semibold text-terracotta shrink-0">
              Active
            </span>
          </button>

          <button
            type="button"
            data-testid="intake-type-change-request"
            aria-disabled="true"
            onClick={() =>
              toast.message(
                "Change requests are coming in the next release. For now, please use Report a Bug."
              )
            }
            className="group relative flex items-center gap-3 px-5 py-4 rounded-2xl bg-sand/60 border border-sage text-left cursor-not-allowed"
          >
            <span className="h-10 w-10 grid place-items-center rounded-xl bg-white border border-sage text-ink-muted shrink-0">
              <Lightbulb className="w-5 h-5" strokeWidth={1.7} />
            </span>
            <div className="min-w-0">
              <p className="font-display text-base text-ink-muted leading-tight">
                Change Request
              </p>
              <p className="text-xs text-ink-muted/80 truncate">
                Suggest a feature or improvement
              </p>
            </div>
            <span className="ml-auto text-[10px] tracking-[0.18em] uppercase font-semibold text-[#7B9672] bg-[#7B9672]/12 px-2.5 py-1 rounded-full shrink-0">
              Soon
            </span>
          </button>
        </section>

        {/* Hero */}
        <section className="mb-10 animate-fade-up">
          <p className="accent-label mb-3">New bug report</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight text-ink leading-tight">
            Describe the problem.{" "}
            <span className="text-terracotta">We'll structure it.</span>
          </h1>
          <p className="mt-4 text-ink-muted max-w-2xl">
            Press the mic and talk it through — what you were doing, what went wrong,
            and what you expected. Add a screenshot if handy. You'll review everything
            before we submit it.
          </p>
        </section>

        {/* Step 1 & 2 */}
        <section className="grid gap-6 animate-fade-up">
          <VoiceRecorder onAudioReady={onRecordedChange} disabled={generating} />
          <AudioUpload onChange={onUploadedChange} disabled={generating} />
          <AttachmentUpload onChange={setAttachment} disabled={generating} />
        </section>

        {/* Generate button */}
        <section className="mt-8 animate-fade-up">
          <div className="bg-white border border-sage rounded-2xl shadow-ambient p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
            <div>
              <p className="accent-label mb-1">Step 03</p>
              <h3 className="font-display text-xl text-ink">
                Turn your voice into a structured report
              </h3>
              <p className="text-sm text-ink-muted mt-1">
                AI will transcribe and fill the template. You can edit anything after.
              </p>
            </div>
            <Button
              onClick={onGenerate}
              disabled={!audioFile || generating}
              data-testid="generate-bug-report-button"
              className="h-12 px-6 rounded-xl bg-terracotta hover:bg-[#C06555] active:bg-[#A85346] text-white disabled:opacity-60 transition-all"
            >
              {generating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" strokeWidth={1.8} />
                  Generating bug report…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" strokeWidth={1.8} />
                  Generate Bug Report
                </>
              )}
            </Button>
          </div>
        </section>

        {/* Preview & submit */}
        {bug && (
          <section
            className="mt-10 animate-fade-up"
            data-testid="bug-review-section"
          >
            <div className="bg-white border border-sage rounded-2xl shadow-ambient p-6 sm:p-10">
              <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
                <div>
                  <p className="accent-label mb-1">Step 04 · review &amp; edit</p>
                  <h3 className="font-display text-2xl sm:text-3xl text-ink">
                    Bug Report Ready for Review
                  </h3>
                  <p className="text-sm text-ink-muted mt-1">
                    Everything editable. Title and "What happened" are required.
                  </p>
                </div>
              </div>

              <BugPreviewForm bug={bug} onChange={setBug} />

              <div className="mt-8 flex flex-col sm:flex-row items-center justify-end gap-3">
                <Button
                  variant="ghost"
                  onClick={() => setBug(null)}
                  data-testid="discard-bug-button"
                  className="rounded-xl text-ink-muted hover:bg-sand hover:text-ink"
                >
                  Discard draft
                </Button>
                <Button
                  onClick={onSubmit}
                  disabled={submitting}
                  data-testid="submit-bug-button"
                  className="h-12 px-7 rounded-xl bg-terracotta hover:bg-[#C06555] active:bg-[#A85346] text-white disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2
                        className="w-4 h-4 mr-2 animate-spin"
                        strokeWidth={1.8}
                      />
                      Submitting…
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" strokeWidth={1.8} />
                      Submit Bug
                    </>
                  )}
                </Button>
              </div>
            </div>
          </section>
        )}

        <footer className="mt-16 text-xs text-ink-muted text-center">
          Voice Bug Intake · Internal MVP
        </footer>
      </main>
    </div>
  );
}

function Header({ onLogout }) {
  return (
    <header className="sticky top-0 z-30 bg-bone/85 backdrop-blur border-b border-sage">
      <div className="max-w-5xl mx-auto flex items-center justify-between px-5 sm:px-8 h-16">
        <div className="flex items-center gap-3">
          <span className="h-9 w-9 grid place-items-center rounded-xl bg-white border border-sage text-terracotta shadow-ambient">
            <Mic className="w-4 h-4" strokeWidth={1.8} />
          </span>
          <span className="font-display text-lg text-ink tracking-tight">
            Voice Bug Intake
          </span>
        </div>
        <Button
          variant="ghost"
          onClick={onLogout}
          data-testid="logout-button"
          className="rounded-xl text-ink-muted hover:bg-sand hover:text-ink"
        >
          <LogOut className="w-4 h-4 mr-2" strokeWidth={1.7} />
          Log out
        </Button>
      </div>
    </header>
  );
}
